import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Chats, ChatCircleDots, PaperPlaneTilt, Spinner, User, ShieldCheck, Paperclip, X, FileText, Circle } from "@phosphor-icons/react";
import { fetchQuoteComments, postQuoteComment, uploadQuoteAttachment, sendTypingHeartbeat, fetchTypingUsers } from "@/lib/api";

/**
 * Inline comments / replies thread on a quote.
 *
 * Access rules:
 *   - Admin (session) → can post without any extra identity
 *   - Logged-in customer (session) → can post without providing email
 *   - Anonymous via magic-link (?t=<token>) → must provide display name + email
 *
 * Props:
 *   quoteId: string
 *   magicToken: string | "" — from useSearchParams().get("t")
 *   user: current session user (null for anonymous / magic-link)
 */
export default function QuoteCommentsThread({ quoteId, magicToken, user }) {
  const isAdmin = Boolean(user?.is_admin);
  const isCustomer = user?.role === "customer";
  const isAnonymous = !user && Boolean(magicToken);
  const canPost = isAdmin || isCustomer || isAnonymous;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [body, setBody] = useState("");
  const [authorName, setAuthorName] = useState(
    isAdmin || isCustomer ? (user?.name || user?.email?.split("@")[0] || "") : ""
  );
  const [authorEmail, setAuthorEmail] = useState(
    isCustomer ? (user?.email || "") : ""
  );
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]); // [{id, filename, size, url, uploading, error}]
  const fileInputRef = useRef(null);

  // ── Typing indicator + unread-count
  const [typingUsers, setTypingUsers] = useState([]);
  // Track the current user's identity key — we use it to exclude ourselves from the list
  const ownKey = isAdmin
    ? `a:${(user?.email || "").toLowerCase()}`
    : isCustomer
      ? `c:${(user?.email || "").toLowerCase()}`
      : isAnonymous
        ? `b:${(authorEmail || `mt:${magicToken.slice(-6)}`).toLowerCase()}`
        : "";

  const lastSeenKey = `usair_quote_seen_${quoteId}`;
  const [lastSeen, setLastSeen] = useState(() => {
    try { return localStorage.getItem(lastSeenKey) || ""; } catch { return ""; }
  });
  const markAllSeen = useCallback(() => {
    const now = new Date().toISOString();
    try { localStorage.setItem(lastSeenKey, now); } catch { /* ignore */ }
    setLastSeen(now);
  }, [lastSeenKey]);

  // Unread = comments newer than lastSeen NOT authored by us
  const unreadCount = useMemo(() => {
    if (!lastSeen) return 0;
    const myEmail = (user?.email || authorEmail || "").toLowerCase();
    return comments.filter((c) => {
      if (!c.created_at) return false;
      if ((c.author_email || "").toLowerCase() === myEmail) return false;
      return c.created_at > lastSeen;
    }).length;
  }, [comments, lastSeen, user, authorEmail]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = magicToken ? { t: magicToken } : {};
      const d = await fetchQuoteComments(quoteId, params);
      setComments(d?.comments || []);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || "Couldn't load comments");
    } finally {
      setLoading(false);
    }
  }, [quoteId, magicToken]);

  useEffect(() => { load(); }, [load]);

  // Mark all current comments as "seen" when the thread has loaded for the first time.
  // We defer by 2s so the user actually has a chance to SEE the unread badge before we clear it.
  useEffect(() => {
    if (loading || comments.length === 0) return;
    const t = setTimeout(() => markAllSeen(), 2000);
    return () => clearTimeout(t);
  }, [loading, comments.length, markAllSeen]);

  // Typing heartbeat: while the user has text in the composer, ping every ~2s.
  useEffect(() => {
    if (!body.trim() || !canPost) return;
    let cancelled = false;
    const ping = async () => {
      try {
        const payload = { name: authorName || user?.name || "", email: authorEmail || user?.email || "" };
        if (magicToken) payload.magic_token = magicToken;
        await sendTypingHeartbeat(quoteId, payload);
      } catch { /* silent */ }
    };
    ping();  // first immediate ping
    const id = setInterval(() => { if (!cancelled) ping(); }, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [body, canPost, quoteId, authorName, authorEmail, magicToken, user]);

  // Poll for other typers every 3s
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const params = {};
        if (magicToken) params.t = magicToken;
        if (ownKey) params.exclude_key = ownKey;
        const d = await fetchTypingUsers(quoteId, params);
        if (!cancelled) setTypingUsers(d?.typers || []);
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [quoteId, magicToken, ownKey]);

  // If the user scrolls to the bottom / has the thread open and new comments arrive, refresh seen
  useEffect(() => {
    if (unreadCount > 0) {
      const t = setTimeout(() => markAllSeen(), 1500);
      return () => clearTimeout(t);
    }
  }, [unreadCount, markAllSeen]);

  const handleSubmit = async () => {
    const text = body.trim();
    setPostError("");
    if (!text) return;
    if (isAnonymous) {
      if (!authorEmail.trim() || !authorEmail.includes("@")) {
        setPostError("Please enter a valid email address");
        return;
      }
      if (!authorName.trim()) {
        setPostError("Please enter your name");
        return;
      }
    }
    setPosting(true);
    try {
      const attachment_ids = pendingFiles
        .filter((f) => f.id && !f.error)
        .map((f) => f.id);
      const payload = {
        body: text,
        author_name: authorName.trim(),
        author_email: authorEmail.trim(),
        attachment_ids,
      };
      if (magicToken) payload.magic_token = magicToken;
      const newComment = await postQuoteComment(quoteId, payload);
      setComments((prev) => [...prev, newComment]);
      setBody("");
      setPendingFiles([]);
    } catch (e) {
      setPostError(e?.response?.data?.detail || e?.message || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    for (const file of files) {
      const tempKey = `tmp-${Date.now()}-${file.name}`;
      setPendingFiles((prev) => [...prev, { tempKey, filename: file.name, size: file.size, uploading: true }]);
      try {
        const uploaded = await uploadQuoteAttachment(quoteId, file, magicToken || "");
        setPendingFiles((prev) => prev.map((f) =>
          f.tempKey === tempKey ? { ...uploaded, tempKey, uploading: false } : f
        ));
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Upload failed";
        setPendingFiles((prev) => prev.map((f) =>
          f.tempKey === tempKey ? { ...f, uploading: false, error: msg } : f
        ));
      }
    }
  };

  const removePendingFile = (tempKey) => {
    setPendingFiles((prev) => prev.filter((f) => f.tempKey !== tempKey));
  };

  return (
    <div data-testid="quote-comments-section" className="bg-white border border-slate-200 rounded-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <ChatCircleDots size={14} weight="bold" className="text-slate-500" />
          <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">Comments & replies</h2>
          <span data-testid="quote-comments-count" className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">
            {comments.length}
          </span>
          {unreadCount > 0 && (
            <span
              data-testid="quote-comments-unread-badge"
              className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-blue-600 px-1.5 py-0.5 rounded-full animate-pulse"
              title="New comments since your last visit"
            >
              <Circle size={6} weight="fill" /> {unreadCount} new
            </span>
          )}
        </div>
        <button
          data-testid="quote-comments-refresh"
          onClick={load}
          className="text-[11px] text-slate-400 hover:text-slate-700 underline"
        >Refresh</button>
      </div>

      {/* Thread */}
      <div className="divide-y divide-slate-100 max-h-[420px] overflow-auto">
        {loading ? (
          <div className="p-6 text-center">
            <Spinner size={16} className="animate-spin text-slate-400 mx-auto" />
          </div>
        ) : loadError ? (
          <div data-testid="quote-comments-error" className="p-4 text-xs text-red-700 bg-red-50 border-y border-red-200">{loadError}</div>
        ) : comments.length === 0 ? (
          <div data-testid="quote-comments-empty" className="p-8 text-center">
            <Chats size={28} weight="thin" className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-600 font-semibold">No comments yet</p>
            <p className="text-[11px] text-slate-400 mt-1">
              {isAdmin ? "Start the conversation with the buyer." :
               isAnonymous ? "Reply to our team with any questions or adjustments." :
               "Add a note below to kick off the discussion."}
            </p>
          </div>
        ) : (
          comments.map((c) => {
            const isUnread = lastSeen && c.created_at > lastSeen &&
              (c.author_email || "").toLowerCase() !== ((user?.email || authorEmail || "").toLowerCase());
            return <CommentBubble key={c.id} comment={c} isUnread={isUnread} />;
          })
        )}
        {/* Typing indicator — shown regardless of empty/non-empty state */}
        {!loading && typingUsers.length > 0 && (
          <div data-testid="quote-typing-indicator" className="px-5 py-2 flex items-center gap-2 text-[11px] text-slate-500 italic">
            <span className="inline-flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "0ms"}}/>
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "150ms"}}/>
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "300ms"}}/>
            </span>
            <span>
              {typingUsers.length === 1
                ? `${typingUsers[0].name} is typing…`
                : typingUsers.length === 2
                  ? `${typingUsers[0].name} and ${typingUsers[1].name} are typing…`
                  : `${typingUsers.length} people are typing…`}
            </span>
          </div>
        )}
      </div>

      {/* Composer */}
      {canPost ? (
        <div className="border-t border-slate-200 p-4 bg-slate-50">
          {isAnonymous && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                data-testid="quote-comment-name-input"
                type="text"
                placeholder="Your name"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                className="h-9 text-sm px-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
              <input
                data-testid="quote-comment-email-input"
                type="email"
                placeholder="Your email"
                value={authorEmail}
                onChange={(e) => setAuthorEmail(e.target.value)}
                className="h-9 text-sm px-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
          )}
          <textarea
            data-testid="quote-comment-body-input"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); handleSubmit(); }
            }}
            placeholder={
              isAdmin ? "Write a reply to the buyer… (Ctrl/⌘ + Enter to send, @name to mention staff)" :
              "Write a message to our team… (Ctrl/⌘ + Enter to send)"
            }
            className="w-full text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y"
          />

          {/* Pending attachments */}
          {pendingFiles.length > 0 && (
            <div data-testid="quote-comment-pending-files" className="flex flex-wrap gap-1.5 mt-2">
              {pendingFiles.map((f) => (
                <span
                  key={f.tempKey}
                  data-testid={`quote-comment-pending-${f.tempKey}`}
                  className={`inline-flex items-center gap-1.5 text-[11px] border px-2 py-1 rounded-sm ${
                    f.error ? "bg-red-50 border-red-200 text-red-700" :
                    f.uploading ? "bg-slate-50 border-slate-200 text-slate-500" :
                    "bg-blue-50 border-blue-200 text-blue-700"
                  }`}
                  title={f.error || f.filename}
                >
                  {f.uploading
                    ? <Spinner size={10} className="animate-spin" />
                    : <FileText size={10} weight="bold" />}
                  <span className="truncate max-w-[180px]">{f.filename}</span>
                  <span className="text-[10px] text-slate-400">{Math.round((f.size || 0) / 1024)} KB</span>
                  <button
                    onClick={() => removePendingFile(f.tempKey)}
                    className="text-slate-400 hover:text-red-600"
                    title="Remove"
                  ><X size={10} /></button>
                </span>
              ))}
            </div>
          )}

          {postError && (
            <p data-testid="quote-comment-error" className="text-[11px] text-red-600 mt-1">{postError}</p>
          )}
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                data-testid="quote-comment-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={handleFilePick}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif"
              />
              <button
                data-testid="quote-comment-attach"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1 h-8 px-2 text-[11px] font-bold rounded-sm border border-slate-300 text-slate-600 hover:bg-white"
                title="Attach files"
              >
                <Paperclip size={11} weight="bold" /> Attach
              </button>
              <p className="text-[10px] text-slate-400 hidden md:block">
                {isAdmin ? "Buyer will receive an email notification." :
                 isAnonymous ? "Our team will be notified by email." :
                 "Our team will receive your reply by email."}
              </p>
            </div>
            <button
              data-testid="quote-comment-submit"
              onClick={handleSubmit}
              disabled={posting || !body.trim() || pendingFiles.some((f) => f.uploading)}
              className="inline-flex items-center gap-1.5 h-9 px-4 text-xs font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {posting ? <><Spinner size={11} className="animate-spin" /> Sending…</> : <><PaperPlaneTilt size={11} weight="bold" /> Send reply</>}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-slate-200 p-4 bg-slate-50 text-[11px] text-slate-400 italic">
          Sign in — or open this quote via a shared link — to join the conversation.
        </div>
      )}
    </div>
  );
}

const ROLE_BADGE = {
  admin:    { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Staff", Icon: ShieldCheck },
  customer: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Customer", Icon: User },
  buyer:    { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Buyer", Icon: User },
};

function CommentBubble({ comment, isUnread }) {
  const role = comment.author_role || "buyer";
  const meta = ROLE_BADGE[role] || ROLE_BADGE.buyer;
  const Icon = meta.Icon;
  const isStaff = role === "admin";
  const align = isStaff ? "items-start" : "items-start";  // keep left-aligned, just color-code
  const bubbleCls = isStaff
    ? "bg-blue-50/60 border-blue-200"
    : role === "customer"
      ? "bg-emerald-50/60 border-emerald-200"
      : "bg-amber-50/40 border-amber-200";
  return (
    <div
      data-testid={`quote-comment-${comment.id}`}
      data-unread={isUnread ? "1" : "0"}
      className={`px-5 py-3 flex ${align} gap-3 ${isUnread ? "bg-blue-50/40 border-l-4 border-l-blue-500" : ""}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border ${meta.cls}`}>
        <Icon size={13} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap mb-1">
          <p className="text-sm font-bold text-slate-900">{comment.author_name || "—"}</p>
          <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${meta.cls}`}>
            {meta.label}
          </span>
          {isUnread && (
            <span className="text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded-sm bg-blue-600 text-white border-blue-600">
              New
            </span>
          )}
          <span className="text-[11px] text-slate-400 ml-auto">{formatCommentTime(comment.created_at)}</span>
        </div>
        <div className={`text-sm text-slate-800 whitespace-pre-wrap border rounded-sm px-3 py-2 ${bubbleCls}`}>
          {renderBodyWithMentions(comment.body)}
        </div>
        {/* Attachments */}
        {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
          <div data-testid={`quote-comment-attachments-${comment.id}`} className="flex flex-wrap gap-1.5 mt-2">
            {comment.attachments.map((a) => {
              const sizeKb = Math.max(1, Math.round((a.size || 0) / 1024));
              return (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 px-2 py-1 rounded-sm transition-colors"
                  title={`${a.filename} (${sizeKb} KB)`}
                >
                  <FileText size={11} weight="bold" />
                  <span className="truncate max-w-[220px]">{a.filename}</span>
                  <span className="text-[10px] text-slate-400">{sizeKb} KB</span>
                </a>
              );
            })}
          </div>
        )}
        {comment.author_email && (
          <p className="text-[10px] text-slate-400 font-mono mt-0.5">{comment.author_email}</p>
        )}
      </div>
    </div>
  );
}

function formatCommentTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return d.toLocaleString();
  } catch { return iso; }
}

// Replaces @mentions with blue pill spans for visual affordance.
function renderBodyWithMentions(body) {
  if (!body) return null;
  const parts = [];
  const re = /@([A-Za-z0-9_.\-]+)/g;
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIdx) parts.push(body.slice(lastIdx, m.index));
    parts.push(
      <span
        key={`mention-${key++}`}
        className="inline-block bg-blue-100 text-blue-800 font-semibold rounded-sm px-1 mx-0.5"
      >@{m[1]}</span>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return parts;
}
